import React, { useEffect, useState } from "react";

import { Driver, DeliveryGroup } from "./model";

interface DeliveryGroupProps {
  groups: DeliveryGroup[];
  allowUngroup?: boolean;
  isUnallocatedOrder: boolean;
  onConfirm: (groupId: number) => Promise<void>;
}

const DeliveryGroupSelect = ({ groups, allowUngroup, isUnallocatedOrder, onConfirm } : DeliveryGroupProps) => {
  const [selectedDeliveryGroupId, setSelectedDeliveryGroupId] = useState<number>(-2);
  const [sending, setSending] = useState(false);

  const handleClick = async (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    event.preventDefault();

    setSending(true);

    try {
      await onConfirm(selectedDeliveryGroupId);
      setSelectedDeliveryGroupId(-2);
    } catch {
      // a decline from `onConfirm`
    } finally {
      setSending(false);
    }
  }

  const handleChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedDeliveryGroupId(parseInt(event.target.value));
    setSending(false);
  }

  const optionNodes = groups.map(group => {
    return (<option value={group.id} key={group.id}>{group.groupNumber}</option>)
  });

  const classes = sending ? "is-loading" : "";
  const nilState = <option value={"-2"}>{isUnallocatedOrder ? "->" : "-"}</option>
  const ungroup = allowUngroup ?
    <option value={"-1"}>Ungroup</option> : <></>;

  return (
    <div className="field has-addons" data-nodnd={true}>
      <div className="control is-expanded">
          <span className="select is-large-control is-small is-fullwidth is-radiusless">
            <select className="is-radiusless pl-1" value={selectedDeliveryGroupId} onChange={handleChange}>
              {nilState}
              {ungroup}
              {optionNodes}
            </select>
          </span>
      </div>

      <div className="control">
        <button className={`button is-large-control is-small ${classes}`} disabled={sending} onClick={(e) => void handleClick(e)}>
            <span className="icon">
              <i className="fas fa-caret-right"></i>
            </span>
        </button>
      </div>
    </div>
  );
}

interface DriverSelectProps {
  drivers: Driver[];
  allowUnallocate?: boolean;
  onConfirm: (driverId: number) => Promise<void>;
  onSelect?: (driverId: number) => Promise<void>;
  storedDriverId?: number | null;
}

const DriverSelect = ({ storedDriverId, drivers, allowUnallocate = false, onConfirm, onSelect } : DriverSelectProps) => {
  const [selectedDriverId, setSelectedDriverId] = useState(-2);
  const [sending, setSending] = useState(false);

  const selectableStoredDriverId = drivers.some(driver => driver.id == storedDriverId) ? storedDriverId : null;

  useEffect(() => {
    setSelectedDriverId(selectableStoredDriverId ?? -2);
  }, [selectableStoredDriverId]);

  const handleClick = async (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    event.preventDefault();

    setSending(true);

    try {
      await onConfirm(selectedDriverId);
    } catch {
      // a decline from `onConfirm`
    } finally {
      setSending(false);
    }
  }

  const handleChange = async (event: React.ChangeEvent<HTMLSelectElement>) => {
    const driverId = parseInt(event.target.value);
    setSelectedDriverId(driverId);

    if (!onSelect) {
      setSending(false);
      return;
    }

    setSending(true);

    try {
      await onSelect(driverId);
    } catch {
      setSelectedDriverId(selectableStoredDriverId ?? -2);
    } finally {
      setSending(false);
    }
  }

  const optionNodes = drivers.map(driver => {
    const label = driver.internalNote == "" ? driver.name : `${driver.name} (!\u20DD ${driver.internalNote.length <= 100 ? driver.internalNote : driver.internalNote.slice(0, 100) + '…'})`;

    let vehicleSuffix;

    switch (driver.vehicleType) {
      case "Scooter":
        vehicleSuffix = <> &#128757;</>;
        break;
      case "Motorbike":
        vehicleSuffix = <> &#127949;</>;
        break;
      case "E-bike":
        vehicleSuffix = <> &#128690;</>;
        break;
      case "E-scooter":
        vehicleSuffix = <> &#128756;</>;
        break;
    }

    return <option key={driver.id} value={driver.id}>{label}{vehicleSuffix}</option>
  });

  const classes = sending ? "is-loading" : "";
  const nilState = <option value={"-2"}>-</option>
  const unallocate = allowUnallocate ?
    <option value={"-1"}>none</option> : <></>;

  return (
    <form>
      <div className="field has-addons" data-nodnd={true}>
        <div className="control is-expanded is-radiusless">
            <span className="select is-large-control is-small is-fullwidth is-radiusless">
              <select className="is-radiusless pl-1" value={selectedDriverId} onChange={(e) => void handleChange(e)}>
                {nilState}
                {unallocate}
                {optionNodes}
              </select>
            </span>
        </div>

        <div className="control">
          <button className={`button is-large-control is-small ${classes}`} disabled={sending} onClick={(e) => void handleClick(e)}>
              <span className="icon">
                <i className="fas fa-caret-right"></i>
              </span>
          </button>
        </div>
      </div>
    </form>
  );
}

export { DeliveryGroupSelect, DriverSelect };
